import { Card, ComponentBoundary, useDialog } from "@fpsoftware/mvp";
import { useEffect, useState } from "react";
import {
  TbAlertTriangle,
  TbArrowsExchange,
  TbPlus,
  TbSettings,
} from "react-icons/tb";
import { formatMoney, formatPercent, sum } from "shared";
import { Hint } from "@/common/Hint";
import { NoContent } from "@/common/NoContent";
import { useCompanyFees } from "@/company/useCompanyFees";
import { Button } from "@/components/Button";
import { Grid } from "@/components/grid/Grid";
import styles from "@/mortgages/OfferList.module.css";
import { usePurchase } from "@/purchases/hooks";
import { ManagePurchaseOfferDialog } from "@/purchases/ManagePurchaseOfferDialog";
import { PrepaidListItem } from "@/purchases/PrepaidListItem";
import {
  useSuspensePurchaseOffer,
  useSuspensePurchaseOfferList,
} from "@/purchases/purchase-offers.hooks";

interface PurchaseOfferListProps {
  purchaseID: number;
}

export const PurchaseOfferList = (props: PurchaseOfferListProps) => {
  const purchase = usePurchase(props.purchaseID);
  const companyFees = useCompanyFees();
  const [offerId, setOfferId] = useState<number | undefined>(undefined);
  const offers = useSuspensePurchaseOfferList(purchase.ID, purchase.offerIDs);
  const { open } = useDialog();

  useEffect(() => {
    if (purchase.offerIDs.length > 0 && offerId === undefined) {
      setOfferId(purchase.offerIDs[0]);
    }

    if (offerId && !purchase.offerIDs.includes(offerId)) {
      setOfferId(undefined);
    }
  }, [purchase.offerIDs, offerId]);

  const handleManageOffer = () => {
    open(ManagePurchaseOfferDialog, {
      purchaseID: props.purchaseID,
      purchasePrice: purchase.price,
      onConfirm: setOfferId,
      offers: offers,
      fees: purchase.fees.length > 0 ? purchase.fees : companyFees,
    });
  };

  return (
    <ComponentBoundary>
      <div
        className="mb-3"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h2>Offers</h2>
        {purchase.offerIDs.length > 0 && (
          <div className={styles.selector}>
            <Button
              icon={TbArrowsExchange}
              size="sm"
              onClick={() =>
                window.open(`/api/purchase/offer/${purchase.ID}/compare`)
              }
            >
              Overview PDF
            </Button>
            <select
              value={offerId}
              onChange={(e) => setOfferId(Number(e.target.value))}
            >
              {purchase.offerIDs.map((offerID) => (
                <OfferOption
                  key={offerID}
                  purchaseID={purchase.ID}
                  offerID={offerID}
                />
              ))}
            </select>
            <Button icon={TbSettings} size="sm" onClick={handleManageOffer}>
              Manage offers
            </Button>
          </div>
        )}
      </div>
      <ComponentBoundary>
        {offerId ? (
          <PurchaseOfferDetail offerID={offerId} purchaseID={purchase.ID} />
        ) : (
          <NoContent
            label="No offers created"
            sublabel="Start off by creating offers for this purchase."
            image="/select-option.svg"
          >
            <Button icon={TbPlus} onClick={handleManageOffer}>
              Create offers
            </Button>
          </NoContent>
        )}
      </ComponentBoundary>
    </ComponentBoundary>
  );
};

function OfferOption(props: { purchaseID: number; offerID: number }) {
  const offer = useSuspensePurchaseOffer(props.purchaseID, props.offerID);

  return <option value={offer?.ID}>{offer?.offer.name}</option>;
}

interface PurchaseOfferDetailProps {
  offerID: number;
  purchaseID: number;
}

function PurchaseOfferDetail(props: PurchaseOfferDetailProps) {
  const offer = useSuspensePurchaseOffer(props.purchaseID, props.offerID);

  if (!offer) {
    return null;
  }

  const prepaidItems = offer.offer.prepaidItems
    .filter((x) => ["FEE", "PREPAID_ITEM"].includes(x.group))
    .filter((x) => !x.financed);
  const discountPoints = offer.offer.prepaidItems.find(
    (x) => x.type === "DiscountPoints",
  );

  const totalFees = sum(prepaidItems.map((x) => x.amount));

  const escrows = offer.offer.prepaidItems
    .filter((x) => ["ESCROW"].includes(x.group))
    .filter((x) => !x.financed);

  return (
    <Grid className="mb-6">
      <Grid.Col xs={4}>
        <Card>
          <Card.Header>Pre Paid items</Card.Header>
          <Card.List>
            {prepaidItems.map((item) => (
              // biome-ignore lint/correctness/useJsxKeyInIterable: There is no ID for prepaid items
              <PrepaidListItem item={item} />
            ))}
            <Card.ListItem label="Total">
              {formatMoney(totalFees)}
            </Card.ListItem>
          </Card.List>
        </Card>
      </Grid.Col>
      <Grid.Col xs={4}>
        <Card className="mb-3">
          <Card.Header>Offer details</Card.Header>
          <Card.List>
            <Card.ListItem label="Investor">
              {offer.offer.investor || "-"}
            </Card.ListItem>
            <Card.ListItem label="Interest rate">
              {formatPercent(offer.offer.interestRate)}
            </Card.ListItem>
            <Card.ListItem
              label={
                <>
                  Discount points
                  <Hint>
                    <strong>Positive:</strong> Customer pays extra
                    <br />
                    <strong>Negative:</strong> Customer receives extra
                  </Hint>
                </>
              }
            >
              {formatPercent(discountPoints?.percentage.beforeCompensation)}
            </Card.ListItem>
            <Card.ListItem label="Discount (after comp.)">
              {formatPercent(discountPoints?.percentage.afterCompensation)}
            </Card.ListItem>
            <Card.ListItem label="Compensation">
              {formatMoney(offer.offer.compensation)}
            </Card.ListItem>
            <Card.ListItem label="Mortgage insurance">
              {formatPercent(offer.mortgageInsurance)}
            </Card.ListItem>
            <Card.ListItem label="Years">{offer.offer.term / 12}</Card.ListItem>
          </Card.List>
        </Card>
        <Card>
          <Card.Header>Escrow</Card.Header>
          <Card.List>
            {escrows.map((item) => (
              // biome-ignore lint/correctness/useJsxKeyInIterable: There is no ID for prepaid items
              <PrepaidListItem item={item} />
            ))}
          </Card.List>
        </Card>
      </Grid.Col>
      <Grid.Col xs={4}>
        {offer.offer.warnings.length > 0 && (
          <Card className="mb-3">
            <Card.Header>Warnings</Card.Header>
            <Card.List>
              {offer.offer.warnings.map((x) => (
                <Card.ListItem label={x} key={x}>
                  <TbAlertTriangle className={styles.error} size={20} />
                </Card.ListItem>
              ))}
            </Card.List>
          </Card>
        )}
        <Card>
          <Card.Header>Summary</Card.Header>
          <Card.List>
            <Card.ListItem label="Base loan amount">
              {formatMoney(offer.offer.amounts.base)}
            </Card.ListItem>
            <Card.ListItem label="Total loan amount">
              {formatMoney(offer.offer.amounts.total)}
            </Card.ListItem>
            <Card.ListItem label="Total prepaids at closing">
              {formatMoney(offer.offer.upfrontPrePaidItems)}
            </Card.ListItem>
            <Card.ListItem label="Total prepaids at closing">
              {formatMoney(offer.offer.downpaymentDue)}
            </Card.ListItem>
            <Card.ListItem label="Amount due at closing">
              {formatMoney(offer.offer.amountDue)}
            </Card.ListItem>
          </Card.List>
        </Card>
      </Grid.Col>
    </Grid>
  );
}
